/*
 * WHICH ZONE THIS SURFACE IS SERVED FROM, and how to name its siblings.
 *
 * The Vitesse cloud is several hosts under one apex — `app.`, `cloud.`, `ai.`, `fleet.`, `plans.` —
 * and a browser on one of them regularly needs the URL of another: the gateway app calls the cloud
 * api-gateway and the AI worker, the fleet admin calls both. Every one of those was written as an
 * equality against a literal host and a literal answer:
 *
 *     hostname() === 'app.vitesseautomation.ai' ? 'https://cloud.vitesseautomation.ai' : sameOrigin()
 *
 * Which is correct exactly once. Serve the same bundle from a different apex and the test does not
 * FAIL — it falls through to same-origin, so the app quietly calls itself for a plane that lives
 * somewhere else. A silent wrong answer is the worst shape available for a rule that is really
 * "my siblings live beside me": the app already knows its own apex, so nothing needed to be
 * configured at all.
 *
 * Here the apex is READ from the location and the sibling is derived beside it. Moving the whole
 * platform to a new apex becomes one entry in the list below rather than a sweep through four apps,
 * and a surface served from an apex nobody listed falls back to same-origin — which is what local
 * development wants, and what an unrecognised host should get anyway.
 *
 * PURE and DOM-free: this module is imported from Web Workers (the app's Backend Worker reads its
 * api-gateway URL through it), so it must never touch `document` — see the subpath note in
 * app-ui/src/core/env.ts.
 */
/**
 * The apexes the Vitesse cloud is served from, most-current first.
 *
 * More than one during a migration: a cutover has a window where both apexes are live — DNS, an
 * issued certificate and a browser's open tab do not move at the same instant — and a list holding
 * only the destination breaks every session still on the old one. Removing the retired entry is the
 * LAST step of a migration, not the first, which is why this survived the .com → .ai move holding
 * the same apex TWICE. One entry, because one apex is live.
 */
export const CLOUD_APEXES = ['vitesseautomation.ai'];
/** The apex this document is served from, or '' when it is not a recognised Vitesse cloud host
 *  (local development, an edge box, a preview deployment — all of which want same-origin). */
export function currentApex(hostname = globalThis.location?.hostname ?? '') {
    // ENDS-WITH IS NOT ENOUGH: `app.local.vitesseautomation.ai` ends with the apex and is a local
    // dev host whose siblings do not exist. A cloud surface is exactly one label deep — `app.<apex>`
    // — so the match is on the whole hostname, which excludes anything nested under it.
    return CLOUD_APEXES.find((apex) => hostname.split('.').length === apex.split('.').length + 1 && hostname.endsWith(`.${apex}`)) ?? '';
}
/**
 * The `https://` URL of a sibling surface under this apex, or '' when there is no apex to be
 * beside.
 *
 * Callers read the empty string as "use same-origin", which keeps the decision — *is a sibling
 * reachable from here* — in one place rather than in each app's ternary.
 */
export function siblingUrl(subdomain, hostname = globalThis.location?.hostname ?? '') {
    const apex = currentApex(hostname);
    return apex ? `https://${subdomain}.${apex}` : '';
}
/**
 * The URL of a sibling SURFACE to navigate a person to — which is not the same question as
 * `siblingUrl`, and must not share its answer.
 *
 * `siblingUrl` answers "where does that PLANE's API live", and for a dev host the answer is
 * deliberately "same origin": the other planes are served through the vite proxy, so pointing at
 * `cloud.local.<apex>` would send every request past the proxy that exists to serve it.
 *
 * A LINK is the opposite case. `ai.local.<apex>` is a real host a person can open, and same-origin
 * is the one answer that cannot work — it appends the console's path to whichever app the reader is
 * already in, which is what made "AI console" and "Memories" unfollowable from the fleet and gateway
 * apps. So this matches the dev zone as well, and returns '' only when there is genuinely nowhere to
 * send them.
 */
export function surfaceUrl(subdomain, hostname = globalThis.location?.hostname ?? '') {
    for (const apex of CLOUD_APEXES) {
        for (const zone of [apex, `local.${apex}`]) {
            // Exactly one label in front of the zone. Anything deeper is not a surface we name, and
            // matching it would invent a host that does not exist.
            if (hostname.endsWith(`.${zone}`) && hostname.split('.').length === zone.split('.').length + 1) {
                return `https://${subdomain}.${zone}`;
            }
        }
    }
    return '';
}
//# sourceMappingURL=zones.js.map